import React, { useState, useRef, useEffect } from 'react';
import { fetchBlockchainOptions, fetchTokenOptions, fetchTokens } from '../../services/defiService';
import { GRADE_OPTIONS, getMinScoreByGrade } from './gradeUtils';

export default function Filters({ 
  selectedToken, 
  setSelectedToken, 
  selectedChain, 
  setSelectedChain,
  selectedProtocol,
  setSelectedProtocol,
  selectedGrade,
  setSelectedGrade,
  protocols = []
}) {
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const [showChainDropdown, setShowChainDropdown] = useState(false);
  const [showProtocolDropdown, setShowProtocolDropdown] = useState(false);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [tokenSearch, setTokenSearch] = useState('');
  const [chainSearch, setChainSearch] = useState('');
  const [protocolSearch, setProtocolSearch] = useState('');
  const [tokens, setTokens] = useState([]);
  const [chains, setChains] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const tokenDropdownRef = useRef(null);
  const chainDropdownRef = useRef(null);
  const protocolDropdownRef = useRef(null);
  const gradeDropdownRef = useRef(null);
  const tokenSearchTimerRef = useRef(null);

  // 加载链选项
  useEffect(() => {
    if (showChainDropdown && chains.length === 0) {
      fetchBlockchainOptions(1, 100).then(res => {
        const chainValues = (res.items || []).map(i => i.value).filter(Boolean);
        setChains(chainValues);
      });
    }
  }, [showChainDropdown, chains.length]);

  // Token 搜索和加载
  useEffect(() => {
    if (!showTokenDropdown) {
      return;
    }

    // 如果有搜索词，使用搜索 API
    if (tokenSearch.trim()) {
      if (tokenSearchTimerRef.current) {
        clearTimeout(tokenSearchTimerRef.current);
      }

      tokenSearchTimerRef.current = setTimeout(() => {
        setTokensLoading(true);
        fetchTokens(tokenSearch, 50, 'count')
          .then(data => {
            if (data.success && Array.isArray(data.tokens)) {
              setTokens(data.tokens.map(t => t.symbol || t));
            } else {
              setTokens([]);
            }
          })
          .catch(() => setTokens([]))
          .finally(() => setTokensLoading(false));
      }, 300);

      return () => {
        if (tokenSearchTimerRef.current) {
          clearTimeout(tokenSearchTimerRef.current);
        }
      };
    } else {
      // 如果没有搜索词，加载热门 token
      setTokensLoading(true);
      fetchTokenOptions(1, 20)
        .then(res => {
          const tokenValues = (res.items || []).map(i => i.value).filter(Boolean);
          setTokens(tokenValues);
        })
        .catch(() => setTokens([]))
        .finally(() => setTokensLoading(false));
    }
  }, [tokenSearch, showTokenDropdown]);

  // 点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target)) {
        setShowTokenDropdown(false);
      }
      if (chainDropdownRef.current && !chainDropdownRef.current.contains(event.target)) {
        setShowChainDropdown(false);
      }
      if (protocolDropdownRef.current && !protocolDropdownRef.current.contains(event.target)) {
        setShowProtocolDropdown(false);
      }
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(event.target)) {
        setShowGradeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredChains = chainSearch
    ? chains.filter(c => c.toLowerCase().includes(chainSearch.toLowerCase()))
    : chains;

  const filteredProtocols = protocolSearch
    ? protocols.filter(p => (p.name || '').toLowerCase().includes(protocolSearch.toLowerCase()))
    : protocols;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
      <div className="flex flex-wrap gap-3 items-stretch">
        {/* Token 筛选 */}
        <div className="flex-1 min-w-[200px] relative" ref={tokenDropdownRef}>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Token
          </label>
          <button
            type="button"
            onClick={() => setShowTokenDropdown(!showTokenDropdown)}
            className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
          >
            <span>{selectedToken || "All Tokens"}</span>
            <svg className={`w-4 h-4 text-gray-500 transition-transform ${showTokenDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showTokenDropdown && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search tokens..."
                  value={tokenSearch}
                  onChange={(e) => setTokenSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto max-h-64">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedToken("");
                    setShowTokenDropdown(false);
                    setTokenSearch("");
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!selectedToken ? 'bg-blue-50 text-blue-600' : ''}`}
                >
                  All Tokens
                </button>
                {tokensLoading ? (
                  <div className="px-4 py-2 text-sm text-gray-500">Loading...</div>
                ) : tokens.length > 0 ? (
                  tokens.map((token) => (
                    <button
                      key={token}
                      type="button"
                      onClick={() => {
                        setSelectedToken(token);
                        setShowTokenDropdown(false);
                        setTokenSearch("");
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedToken === token ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      {token}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">No tokens found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Chain 筛选 */}
        <div className="flex-1 min-w-[200px] relative" ref={chainDropdownRef}>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Chain
          </label>
          <button
            type="button"
            onClick={() => setShowChainDropdown(!showChainDropdown)}
            className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
          >
            <span>{selectedChain || "All Chains"}</span>
            <svg className={`w-4 h-4 text-gray-500 transition-transform ${showChainDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showChainDropdown && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search chains..."
                  value={chainSearch}
                  onChange={(e) => setChainSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto max-h-64">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedChain("");
                    setShowChainDropdown(false);
                    setChainSearch("");
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!selectedChain ? 'bg-blue-50 text-blue-600' : ''}`}
                >
                  All Chains
                </button>
                {filteredChains.length > 0 ? (
                  filteredChains.map((chain) => (
                    <button
                      key={chain}
                      type="button"
                      onClick={() => {
                        setSelectedChain(chain);
                        setShowChainDropdown(false);
                        setChainSearch("");
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedChain === chain ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      {chain}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">No chains found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Protocol 筛选 */}
        <div className="flex-1 min-w-[200px] relative" ref={protocolDropdownRef}>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Protocol
          </label>
          <button
            type="button"
            onClick={() => setShowProtocolDropdown(!showProtocolDropdown)}
            className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
          >
            <span>{selectedProtocol ? protocols.find(p => p.id === selectedProtocol)?.name || "Unknown" : "All Protocols"}</span>
            <svg className={`w-4 h-4 text-gray-500 transition-transform ${showProtocolDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showProtocolDropdown && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
              <div className="p-2 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Search protocols..."
                  value={protocolSearch}
                  onChange={(e) => setProtocolSearch(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto max-h-64">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProtocol(null);
                    setShowProtocolDropdown(false);
                    setProtocolSearch("");
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${!selectedProtocol ? 'bg-blue-50 text-blue-600' : ''}`}
                >
                  All Protocols
                </button>
                {filteredProtocols.length > 0 ? (
                  filteredProtocols.map((protocol) => (
                    <button
                      key={protocol.id}
                      type="button"
                      onClick={() => {
                        setSelectedProtocol(protocol.id);
                        setShowProtocolDropdown(false);
                        setProtocolSearch("");
                      }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedProtocol === protocol.id ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      {protocol.name}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500">No protocols found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Grade 筛选 */}
        <div className="flex-1 min-w-[200px] relative" ref={gradeDropdownRef}>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
            Rating
          </label>
          <button
            type="button"
            onClick={() => setShowGradeDropdown(!showGradeDropdown)}
            className="w-full h-10 pl-3.5 pr-9 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-left flex items-center justify-between"
          >
            <span>{selectedGrade ? GRADE_OPTIONS.find(opt => opt.value === selectedGrade)?.label || 'Any' : 'Any'}</span>
            <svg className={`w-4 h-4 text-gray-500 transition-transform ${showGradeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showGradeDropdown && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
              <div className="overflow-y-auto max-h-64">
                {GRADE_OPTIONS.map((option) => (
                  <button
                    key={option.value || 'any'}
                    type="button"
                    onClick={() => {
                      setSelectedGrade(option.value);
                      setShowGradeDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${selectedGrade === option.value ? 'bg-blue-50 text-blue-600' : ''}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

